import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { ROUTES } from "@/shared/constants/routes";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";

interface FailureCardProps {
  title: string;
  /** What to do next, in plain words. */
  description: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** Extra way-forward actions; the tenants link is always offered unless `hideHome`. */
  actions?: ReactNode;
  hideHome?: boolean;
  /** Technical detail (code, request id, stack) — collapsed by default, never the headline. */
  details?: string | null;
  /** Inline inside a list or tile rather than a full page. */
  compact?: boolean;
  className?: string;
}

/** The ONE failure card: error boundary, router error element and 404 all render it (rules/01 §4). */
export function FailureCard({
  title,
  description,
  onRetry,
  retryLabel = "Try again",
  actions,
  hideHome = false,
  details,
  compact = false,
  className,
}: FailureCardProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col gap-3 rounded-md border border-danger/40 bg-surface text-fg",
        compact ? "p-4" : "mx-auto max-w-lg p-6",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-danger-soft text-danger-fg">
          <Icon icon={AlertTriangle} size={16} />
        </span>
        <div className="min-w-0">
          <p className={cn("font-semibold", compact ? "text-body" : "text-section")}>{title}</p>
          <p className="mt-0.5 text-cell text-fg-muted">{description}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pl-11">
        {onRetry ? (
          <Button variant="primary" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : null}
        {actions}
        {!hideHome ? (
          <Button asChild variant="secondary" size="sm">
            <Link to={ROUTES.tenants}>Go to tenants</Link>
          </Button>
        ) : null}
      </div>
      {details ? (
        <details className="pl-11 text-caption text-fg-muted">
          <summary className="cursor-pointer select-none">Technical details</summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-border bg-bg p-2 font-mono">
            {details}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
